import { useEffect } from 'react';

import { useStore } from 'zustand';

import { isSSR } from '~/shared/utils/platform/utils/platform';

import { cookieConsentStateStore } from './useCookieConsentState';

export type CookieInformationConsents = {
	cookie_cat_necessary: boolean;
	cookie_cat_functional: boolean;
	cookie_cat_marketing: boolean;
	cookie_cat_unclassified: boolean;
	cookie_cat_statistic: boolean;
};

const defaultConsent: CookieInformationConsents = {
	cookie_cat_necessary: false,
	cookie_cat_functional: false,
	cookie_cat_marketing: false,
	cookie_cat_unclassified: false,
	cookie_cat_statistic: false,
};

let eventsBound = false;

// The logic inside this function ran for every single instantiation of the hook below, which was probably not all that
// great for performance. I've moved it here, ensuring it runs only once.
const bindCookieEvents = () => {
	if (isSSR || eventsBound) return;

	eventsBound = true;

	let ready = !!(window as any).cicc;

	const handleConsentChange = (isReady?: boolean) => {
		const consent: typeof defaultConsent = (window as any).cicc ?? defaultConsent;

		if (isReady) ready = true;

		cookieConsentStateStore.setState({
			updated: ready,
			allowFunctional: consent.cookie_cat_functional,
			allowMarketing: consent.cookie_cat_marketing,
			allowNecessary: consent.cookie_cat_necessary,
			allowStatistics: consent.cookie_cat_statistic,
			allowUnclassified: consent.cookie_cat_unclassified,
		});
	};

	const consentCallback = () => handleConsentChange();

	window.addEventListener('CookieInformationConsentGiven', consentCallback);

	// Usually, we'd expect Cookie Information to be ready about now.
	if ((window as any).cicc) {
		handleConsentChange(true);
	} else {
		// ... but if not, we set a timeout to poll for it a few times.
		let count = 0;
		const callback = () => {
			if ((window as any).cicc) handleConsentChange(true);
			else {
				count += 1;
				if (count < 10) {
					window.setTimeout(callback, 250);
				} else {
					// Seems like Cookie Information is not going to load, so set default values instead.
					handleConsentChange(true);
				}
			}
		};
		callback();
	}
};

export const useCookieConsent = () => {
	const store = useStore(cookieConsentStateStore);

	useEffect(() => {
		bindCookieEvents();
	}, []);

	return store;
};
